import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  CorporateAccountOpenProfileDetailsPages,
  useCorporateAccountOpenProfileDetailsPageTypeStore,
} from 'views/pages/accountOpenProfileDetails/corporateProfileDetails/hooks';
import { useEffect } from 'react';
import { useCorporateAccountEstablishStore } from '../hooks';
import { ErrorWord, getMaxLengthErrorMessage, TemplateProps, yapStringWhen, yupNumberString } from '../common';
import { AddressInfo } from 'views/templates/module/addressInput/hook';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';

export type DealerInfoType = {
  dealerFirstName: string;
  dealerLastName: string;
  dealerFirstNameKana: string;
  dealerLastNameKana: string;
  dealerBirthDay: Date | null;
  dealerPostCode: string;
  dealerPrefecture: string;
  dealerCityCountry: string;
  dealerTownStreetAddress: string;
  hasNoDealerTownStreetAddress: boolean; // リクエストに不要
  dealerBuildingNameAndRoomNumber: string;
  dealerMobilePhoneNumber: string;
  dealerMailAddress: string;
  dealerConfirmMailAddress: string; // リクエストに不要
  dealerDepartment: string;
  dealerOtherDepartment: string;
  dealerPosition: string;
};

export const useDealerInfo = ({ isUpdate }: TemplateProps) => {
  const { param, add } = useCorporateAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();
  const { messageClose } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validation = Yup.object().shape({
    dealerFirstName: Yup.string().required(ErrorWord.INPUT).max(40, getMaxLengthErrorMessage(40)),
    dealerLastName: Yup.string().required(ErrorWord.INPUT).max(80, getMaxLengthErrorMessage(80)),
    dealerFirstNameKana: Yup.string()
      .required(ErrorWord.INPUT)
      .matches(/^[ァ-ヴー_]+$/, ErrorWord.FURIGANA_KATAKANA)
      .max(20, getMaxLengthErrorMessage(20)),
    dealerLastNameKana: Yup.string()
      .required(ErrorWord.INPUT)
      .matches(/^[ァ-ヴー_]+$/, ErrorWord.FURIGANA_KATAKANA)
      .max(20, getMaxLengthErrorMessage(20)),
    dealerBirthDay: Yup.date().required(ErrorWord.Date),
    dealerPostCode: yupNumberString({ length: 7, isRequired: true, errorWord: ErrorWord.POSTCODE }),
    dealerPrefecture: Yup.string().required(ErrorWord.SELECT),
    dealerCityCountry: Yup.string().required(ErrorWord.INPUT).max(40, getMaxLengthErrorMessage(40)),
    dealerTownStreetAddress: yapStringWhen('hasNoDealerTownStreetAddress', false, ErrorWord.INPUT).max(
      255,
      getMaxLengthErrorMessage(255),
    ),
    dealerBuildingNameAndRoomNumber: Yup.string().max(50, getMaxLengthErrorMessage(50)),
    dealerMobilePhoneNumber: yupNumberString({ length: 11, isRequired: true, errorWord: ErrorWord.MOBILE_PHONE }),
    dealerMailAddress: Yup.string()
      .trim()
      .required(ErrorWord.EMAIL)
      .email(ErrorWord.EMAIL)
      .max(80, getMaxLengthErrorMessage(80)),
    dealerConfirmMailAddress: Yup.string()
      .required(ErrorWord.EMAIL_CONFIRM)
      .oneOf([Yup.ref('dealerMailAddress')], ErrorWord.EMAIL_CONFIRM),
    dealerDepartment: Yup.string().required(ErrorWord.INPUT).max(80, getMaxLengthErrorMessage(80)),
    dealerOtherDepartment: Yup.string().max(80, getMaxLengthErrorMessage(80)),
    dealerPosition: Yup.string().required(ErrorWord.INPUT).max(128, getMaxLengthErrorMessage(128)),
  });

  const initialValues: DealerInfoType = {
    dealerFirstName: param.dealerFirstName ?? '',
    dealerLastName: param.dealerLastName ?? '',
    dealerFirstNameKana: param.dealerFirstNameKana ?? '',
    dealerLastNameKana: param.dealerLastNameKana ?? '',
    dealerBirthDay: param.dealerBirthDay ?? null,
    dealerPostCode: param.dealerPostCode ?? '',
    dealerPrefecture: param.dealerPrefecture ?? '',
    dealerCityCountry: param.dealerCityCountry ?? '',
    dealerTownStreetAddress: param.dealerTownStreetAddress ?? '',
    hasNoDealerTownStreetAddress: param.hasNoDealerTownStreetAddress ?? false,
    dealerBuildingNameAndRoomNumber: param.dealerBuildingNameAndRoomNumber ?? '',
    dealerMobilePhoneNumber: param.dealerMobilePhoneNumber ?? '',
    dealerMailAddress: param.dealerMailAddress ?? (isUpdate ? '' : param.mailAddressQuestion),
    dealerConfirmMailAddress: param.dealerConfirmMailAddress ?? (isUpdate ? '' : param.mailAddressQuestion),
    dealerDepartment: param.dealerDepartment ?? '',
    dealerOtherDepartment: param.dealerOtherDepartment ?? '',
    dealerPosition: param.dealerPosition ?? '',
  };

  const formik = useFormik<DealerInfoType>({
    enableReinitialize: isUpdate,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.rulerInfo);
    },
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      // バリデーションかける時に必ずエラートーストを閉じる
      messageClose();
    },
  });

  const dealerFirstNameProps = {
    value: formik.values.dealerFirstName,
    onChange: (value: string) => formik.setFieldValue('dealerFirstName', value),
    error: formik.errors.dealerFirstName,
  };

  const dealerLastNameProps = {
    value: formik.values.dealerLastName,
    onChange: (value: string) => formik.setFieldValue('dealerLastName', value),
    error: formik.errors.dealerLastName,
  };

  const dealerFirstNameKanaProps = {
    value: formik.values.dealerFirstNameKana,
    onChange: (value: string) => formik.setFieldValue('dealerFirstNameKana', value),
    error: formik.errors.dealerFirstNameKana,
  };

  const dealerLastNameKanaProps = {
    value: formik.values.dealerLastNameKana,
    onChange: (value: string) => formik.setFieldValue('dealerLastNameKana', value),
    error: formik.errors.dealerLastNameKana,
  };

  const dealerBirthDayProps = {
    date: formik.values.dealerBirthDay,
    name: 'representBirthDay',
    minDate: new Date('1900/1/1'),
    maxDate: new Date(),
    onChange: (value: any) => formik.setFieldValue('dealerBirthDay', value),
    onBlur: formik.handleBlur,
    isWeekEndDisable: false,
    error: formik.errors.dealerBirthDay,
  };

  const dealerAddressPorps = {
    postCode: {
      value: formik.values.dealerPostCode,
      onChange: (value: string) => {
        formik.setFieldValue('dealerPostCode', value);
      },
      error: formik.errors.dealerPostCode,
    },
    prefecture: {
      selectedOption: formik.values.dealerPrefecture,
      setSelectedOption: (value: string) => formik.setFieldValue('dealerPrefecture', value),
      error: formik.errors.dealerPrefecture,
    },
    municipality: {
      value: formik.values.dealerCityCountry,
      onChange: (value: string) => {
        formik.setFieldValue('dealerCityCountry', value);
      },
      error: formik.errors.dealerCityCountry,
    },
    streetAddress: {
      value: formik.values.dealerTownStreetAddress,
      onChange: (value: string) => {
        formik.setFieldValue('dealerTownStreetAddress', value);
      },
      error: formik.errors.dealerTownStreetAddress,
      disabled: formik.values.hasNoDealerTownStreetAddress,
    },
    hasNoTownStreetAddress: {
      checked: formik.values.hasNoDealerTownStreetAddress,
      setChecked: (value: boolean) => {
        formik.setFieldValue('dealerTownStreetAddress', '');
        formik.setFieldValue('hasNoDealerTownStreetAddress', value);
      },
    },
    buildingNameAndRoomNumber: {
      value: formik.values.dealerBuildingNameAndRoomNumber,
      onChange: (value: string) => formik.setFieldValue('dealerBuildingNameAndRoomNumber', value),
      error: formik.errors.dealerBuildingNameAndRoomNumber,
    },
    onSearchAddress: (addressInfo: AddressInfo) => {
      formik.setFieldValue('dealerPrefecture', addressInfo.prefecture);
      formik.setFieldValue('dealerCityCountry', addressInfo.cityCountry);
      formik.setFieldValue('hasNoDealerTownStreetAddress', false);
      formik.setFieldValue('dealerTownStreetAddress', addressInfo.townStreetAddress);
      formik.setFieldValue('dealerBuildingNameAndRoomNumber', '');
    },
  };

  const dealerMobilePhoneNumberProps = {
    value: formik.values.dealerMobilePhoneNumber,
    onChange: (value: string) => formik.setFieldValue('dealerMobilePhoneNumber', value),
    error: formik.errors.dealerMobilePhoneNumber,
  };

  const dealerMailAddressPorps = {
    value: formik.values.dealerMailAddress,
    onChange: (value: string) => formik.setFieldValue('dealerMailAddress', value),
    error: formik.errors.dealerMailAddress,
    disabled: !isUpdate,
  };

  const dealerConfirmMailAddressPorps = {
    value: formik.values.dealerConfirmMailAddress,
    onChange: (value: string) => formik.setFieldValue('dealerConfirmMailAddress', value),
    error: formik.errors.dealerConfirmMailAddress,
  };

  const dealerDepartmentProps = {
    value: formik.values.dealerDepartment,
    onChange: (value: string) => formik.setFieldValue('dealerDepartment', value),
    error: formik.errors.dealerDepartment,
  };
  const dealerOtherDepartmentProps = {
    value: formik.values.dealerOtherDepartment,
    onChange: (value: string) => formik.setFieldValue('dealerOtherDepartment', value),
    error: formik.errors.dealerOtherDepartment,
  };

  const dealerPositionProps = {
    value: formik.values.dealerPosition,
    onChange: (value: string) => formik.setFieldValue('dealerPosition', value),
    error: formik.errors.dealerPosition,
  };

  const goBackProps = {
    onClick: () => {
      if (param.dealerQuestion) {
        setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.contactInfo);
      } else {
        setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.representInfo);
      }
    },
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    dealerFirstNameProps,
    dealerLastNameProps,
    dealerFirstNameKanaProps,
    dealerLastNameKanaProps,
    dealerBirthDayProps,
    dealerAddressPorps,
    dealerMobilePhoneNumberProps,
    dealerMailAddressPorps,
    dealerConfirmMailAddressPorps,
    dealerDepartmentProps,
    dealerOtherDepartmentProps,
    dealerPositionProps,
    goBackProps,
  };
};
